import { supabase } from "../lib/supabase";
import { normalizeEstimate } from "../pricing/utils/normalizeEstimate";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

async function readJsonResponse(response, fallbackMessage) {
  const rawText = await response.text();

  console.log(`RAW ${response.url} RESPONSE:`, rawText);

  let data = null;

  try {
    data = rawText ? JSON.parse(rawText) : null;
  } catch {
    throw new Error(
      `${fallbackMessage}. Backend returned invalid or empty JSON. Check server terminal.`
    );
  }

  if (!response.ok) {
    throw new Error(data?.details || data?.error || fallbackMessage);
  }

  return data;
}

async function uploadQuoteImages(images = []) {
  if (!images.length) return [];

  const uploadedUrls = [];

  for (const file of images) {
    const safeName = `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}-${file.name.replace(/\s+/g, "-")}`;

    const filePath = `quotes/${safeName}`;

    const { error: uploadError } = await supabase.storage
      .from("quote-images")
      .upload(filePath, file, {
        upsert: false,
        contentType: file.type,
      });

    if (uploadError) {
      throw new Error(uploadError.message);
    }

    const { data } = supabase.storage
      .from("quote-images")
      .getPublicUrl(filePath);

    uploadedUrls.push(data.publicUrl);
  }

  return uploadedUrls;
}

async function createQuoteVersion(quote) {
  if (!quote?.id) return null;

  const payload = {
    quote_id: quote.id,

    workspace_id: quote.workspace_id || null,

    request_text: quote.request_text || "",
    parsed_json: quote.parsed_json || {},
    estimate_json: quote.estimate_json || {},
    quote_text: quote.quote_text || "",
    image_urls: quote.image_urls || [],
    status: quote.status || "draft",
  };

  const { data, error } = await supabase
    .from("quote_versions")
    .insert([payload])
    .select()
    .single();

  if (error) {
    console.error("Insert into quote_versions failed:", error, payload);
    throw new Error(error.message);
  }

  return data;
}

async function getQuoteById(id, workspaceId) {
  const { data, error } = await supabase
    .from("quotes")
    .select("*")
    .eq("id", id)
    .eq("workspace_id", workspaceId)
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
}

export async function updateQuote(
  id,
  {
    parsedQuote,
    estimate,
    quoteText,
    requestText,
    imageUrls = [],
    status = "draft",
    workspace_id = null,
    customer_id = null,
    customerId = null,
    customerSnapshot = null,
    customer_snapshot = null,
  }
) {
  const existingQuote = await getQuoteById(id, workspace_id);

  await createQuoteVersion(existingQuote);

  const normalizedParsedQuote = parsedQuote || {};

const baseNormalizedEstimate = estimate
  ? normalizeEstimate(estimate)
  : {};

const normalizedEstimate = estimate
  ? {
      ...baseNormalizedEstimate,
      summary: estimate.summary || baseNormalizedEstimate.summary || {},
    }
  : {};

  const resolvedCustomerId =
  customer_id ||
  customerId ||
  normalizedParsedQuote?.customer?.id ||
  normalizedParsedQuote?.customerId ||
  normalizedParsedQuote?.customer_id ||
  null;

const resolvedCustomerSnapshot =
  customer_snapshot ||
  customerSnapshot ||
  normalizedParsedQuote?.customer ||
  normalizedParsedQuote?.customerSnapshot ||
  null;

const payload = {
  request_text: requestText,

  parsed_json: {
    ...normalizedParsedQuote,

    workspace_id,

    customerId: resolvedCustomerId,
    customer_id: resolvedCustomerId,

    customer: resolvedCustomerSnapshot,
    customerSnapshot: resolvedCustomerSnapshot,
  },

  estimate_json: normalizedEstimate,
  quote_text: quoteText,
  image_urls: imageUrls,
  status,

  workspace_id,

  customer_id: resolvedCustomerId,
  customer_snapshot: resolvedCustomerSnapshot,
};

  const { data, error } = await supabase
  .from("quotes")
  .update({
    request_text: requestText,
    parsed_json: {
      ...normalizedParsedQuote,
      customerId: resolvedCustomerId,
      customer_id: resolvedCustomerId,
      customer: resolvedCustomerSnapshot,
    },
    estimate_json: normalizedEstimate,
    quote_text: quoteText,
    image_urls: imageUrls,
    status,

    workspace_id,

    customer_id: resolvedCustomerId,
    customer_snapshot: resolvedCustomerSnapshot,
  })

    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
}

export async function saveQuote({
  requestText,
  parsedQuote,
  estimate,
  quoteText,
  images = [],
  status = "draft",
  workspace_id = null,
  customer_id = null,
  customerId = null,
  customerSnapshot = null,
  customer_snapshot = null,
}) {
  console.log("saveQuote:start", {
    requestText,
    imageCount: images.length,
    status,
    workspace_id,
  });

  let imageUrls = [];

  try {
    if (images.length > 0) {
      console.log("Uploading images...");
      imageUrls = await uploadQuoteImages(images);
      console.log("Images uploaded:", imageUrls);
    }
  } catch (error) {
    console.error("Image upload failed:", error);
    imageUrls = [];
  }

  const normalizedParsedQuote = parsedQuote || {};

const baseNormalizedEstimate = estimate
  ? normalizeEstimate(estimate)
  : {};

const normalizedEstimate = estimate
  ? {
      ...baseNormalizedEstimate,
      summary: estimate.summary || baseNormalizedEstimate.summary || {},
    }
  : {};

  const resolvedCustomerId =
    customer_id ||
    customerId ||
    normalizedParsedQuote?.customer?.id ||
    normalizedParsedQuote?.customerId ||
    normalizedParsedQuote?.customer_id ||
    null;

  const resolvedCustomerSnapshot =
    customer_snapshot ||
    customerSnapshot ||
    normalizedParsedQuote?.customer ||
    normalizedParsedQuote?.customerSnapshot ||
    null;

  const payload = {
    request_text: requestText,

    parsed_json: {
      ...normalizedParsedQuote,
      workspace_id,
      customerId: resolvedCustomerId,
      customer_id: resolvedCustomerId,
      customer: resolvedCustomerSnapshot,
      customerSnapshot: resolvedCustomerSnapshot,
    },

    estimate_json: normalizedEstimate,
    quote_text: quoteText,
    image_urls: imageUrls,
    status,

    workspace_id,

    customer_id: resolvedCustomerId,
    customer_snapshot: resolvedCustomerSnapshot,
  };

  const { data, error } = await supabase
    .from("quotes")
    .insert([payload])
    .select()
    .single();

  if (error) {
    console.error("Quotes insert failed:", error, payload);
    throw new Error(`Saving quote failed: ${error.message}`);
  }

  console.log("Quote saved:", data);

  try {
    await createQuoteVersion(data);
    console.log("Version saved");
  } catch (error) {
    console.error("Version creation failed:", error);
  }

  return data;
}

export async function extractQuote(requestText, images = []) {
  const formData = new FormData();
  formData.append("requestText", requestText);

  images.forEach((file) => {
    formData.append("images", file);
  });

  const response = await fetch(`${API_BASE_URL}/api/ai/extract-quote`, {
    method: "POST",
    body: formData,
  });

  return readJsonResponse(response, "AI request failed");
}

export async function draftQuoteText(parsedQuote, estimate) {
  const normalizedParsedQuote = parsedQuote || {};
  const normalizedEstimate = estimate ? normalizeEstimate(estimate) : {};

  const response = await fetch(`${API_BASE_URL}/api/ai/draft-quote-text`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      parsedQuote: normalizedParsedQuote,
      estimate: normalizedEstimate,
    }),
  });

  return readJsonResponse(response, "Quote text request failed");
}

export async function regenerateQuoteText(parsed, estimate, options = {}) {
  const normalizedParsed = parsed || {};
  const normalizedEstimate = estimate ? normalizeEstimate(estimate) : {};

  const response = await fetch(`${API_BASE_URL}/api/ai/generate-quote-text`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      parsed: normalizedParsed,
      parsedData: normalizedParsed,
      estimate: normalizedEstimate,
      options: {
        tone: options.tone || "premium",
        ...options,
      },
    }),
  });

  return readJsonResponse(response, "Failed to regenerate quote text");
}

export async function getQuoteVersions(quoteId) {
  const { data, error } = await supabase
    .from("quote_versions")
    .select("*")
    .eq("quote_id", quoteId)
    .order("created_at", { ascending: false });

  if (error) {
    throw new Error(error.message);
  }

  return data || [];
}